/* global Excel Office console setTimeout */
import { format, parse } from "date-fns";

export type TableHeader = {
  header: string;
  key: string;
  type?: string;
};

export type WriteProgress = (written: number, total: number) => void;

/**
 * Rows pushed to Excel per context.sync(). Every sync blocks the Excel host while it
 * processes the batch, so this trades round-trip overhead against how long the UI is
 * frozen at a time. 2000 rows keeps each pause short enough to feel responsive.
 */
const CHUNK_ROWS = 2000;

/**
 * autofitColumns() costs roughly one measurement per cell, so on large results we only
 * fit against the header row instead of the whole table.
 */
const AUTOFIT_MAX_ROWS = 5000;

const supportsExcelApi = (version: string) => {
  try {
    return Office.context.requirements.isSetSupported("ExcelApi", version);
  } catch (error) {
    return false;
  }
};

/**
 * Both of these reset on every context.sync(), so they have to be re-applied before each batch.
 */
const suspendHostUpdates = (context: Excel.RequestContext) => {
  if (supportsExcelApi("1.6")) {
    context.application.suspendApiCalculationUntilNextSync();
  }
  if (supportsExcelApi("1.9")) {
    context.application.suspendScreenUpdatingUntilNextSync();
  }
};

/** Hand the event loop back to the webview so the task pane can repaint between batches. */
const yieldToHost = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

/**
 * Clears the active worksheet. Note that worksheet.getRange() with no address returns the
 * *entire* sheet (over 17 billion cells), which is why this goes through the used range.
 */
export const deleteTable = async () => {
  try {
    await Excel.run(async (context) => {
      const currentWorksheet = context.workbook.worksheets.getActiveWorksheet();

      // eslint-disable-next-line office-addins/load-object-before-read, office-addins/call-sync-before-read
      const tables = currentWorksheet.tables;
      tables.load("items/name");
      const usedRange = currentWorksheet.getUsedRangeOrNullObject();
      // eslint-disable-next-line office-addins/no-navigational-load
      usedRange.load("isNullObject");
      await context.sync();

      suspendHostUpdates(context);
      // Tables survive a range clear, and a leftover table would make tables.add() fail on
      // the name collision, so drop them explicitly.
      tables.items.forEach((table) => table.delete());
      if (!usedRange.isNullObject) {
        usedRange.clear();
      }
      await context.sync();
    });
  } catch (error) {
    console.log("Error: " + error);
    throw error;
  }
};

const convertDate = (date: string) => {
  return date ? format(parse(date, "dd/MM/yyyy", new Date()), "MM/dd/yyyy") : date;
};

const toRow = (item: any, headers: TableHeader[]) =>
  headers.map((header) => {
    if (header.type === "date") {
      return convertDate(item[header.key]);
    }
    const value = item[header.key];
    // Excel rejects undefined/objects in range.values; everything else goes through as-is.
    if (value === undefined || value === null) return "";
    return typeof value === "object" ? JSON.stringify(value) : value;
  });

const sanitizeTableName = (name: string) => {
  // Excel table names: no spaces, max 255 chars, cannot start with a digit or look like a cell ref.
  const cleaned = (name || "").replace(/[^\w.]/g, "_").slice(0, 255);
  return /^[A-Za-z_]/.test(cleaned) ? cleaned : `table_${cleaned}`;
};

/**
 * Writes the result set to the active worksheet in batches instead of one giant
 * table.rows.add() call, which is what made multi-MB results freeze Excel.
 */
const insertData = async (
  headers: TableHeader[],
  data: any[],
  tableName: string,
  onProgress?: WriteProgress
): Promise<void> => {
  const columnCount = headers.length;
  if (columnCount === 0) return;

  const rowCount = data.length;

  try {
    await Excel.run(async (context) => {
      const currentWorksheet = context.workbook.worksheets.getActiveWorksheet();

      suspendHostUpdates(context);
      currentWorksheet.getRangeByIndexes(0, 0, 1, columnCount).values = [headers.map((item) => item.header)];
      await context.sync();

      for (let offset = 0; offset < rowCount; offset += CHUNK_ROWS) {
        // Map one chunk at a time so the full result set is never duplicated in memory.
        const chunk = data.slice(offset, offset + CHUNK_ROWS).map((item) => toRow(item, headers));

        suspendHostUpdates(context);
        // +1 for the header row.
        currentWorksheet.getRangeByIndexes(offset + 1, 0, chunk.length, columnCount).values = chunk;
        // Syncing per chunk is deliberate: one giant batch is exactly what locks up Excel.
        // eslint-disable-next-line office-addins/no-context-sync-in-loop
        await context.sync();

        if (onProgress) onProgress(Math.min(offset + CHUNK_ROWS, rowCount), rowCount);
        await yieldToHost();
      }
    });

    // Building the table and autofitting are separate passes: they are the slow, purely
    // cosmetic part, and by now the data is already visible on the sheet.
    await Excel.run(async (context) => {
      const currentWorksheet = context.workbook.worksheets.getActiveWorksheet();

      suspendHostUpdates(context);
      const tableRange = currentWorksheet.getRangeByIndexes(0, 0, rowCount + 1, columnCount);
      const table = currentWorksheet.tables.add(tableRange, true /*hasHeaders*/);

      if (rowCount <= AUTOFIT_MAX_ROWS) {
        tableRange.format.autofitColumns();
      } else {
        currentWorksheet.getRangeByIndexes(0, 0, 1, columnCount).format.autofitColumns();
      }
      await context.sync();

      // A duplicate or otherwise invalid name only fails on sync, so keep it off the batch above.
      try {
        table.name = sanitizeTableName(tableName);
        await context.sync();
      } catch (error) {
        console.log("Could not name table: " + error);
      }
    });
  } catch (error) {
    console.log("Error: " + error);
    throw error;
  }
};

export default insertData;
